import { useState } from "react";
import { Loader2, CheckCircle2, Upload, FileSpreadsheet, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function EncaissementsImportDialog({ open, onClose, onImport, monthLabel }) {
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState("");

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportError("");
    setImportResult(null);
    try {
      const result = await onImport(importFile);
      setImportResult(result);
      setImportFile(null);
    } catch (e) {
      setImportError(e.message || "Erreur lors de l'import");
    }
    setImporting(false);
  };

  const handleClose = () => {
    setImportFile(null);
    setImportResult(null);
    setImportError("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg" data-testid="import-enc-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#f7c935]" /> Importer des encaissements
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-3">
          <p className="text-xs text-slate-500">Importez un fichier CSV ou XLSX contenant vos encaissements. Les colonnes seront automatiquement mappees.</p>
          <div className="text-xs text-slate-500">Periode cible: <span className="text-[#f7c935] font-bold capitalize">{monthLabel}</span></div>

          {!importResult && (
            <>
              <label className="block cursor-pointer">
                <input type="file" accept=".csv,.xlsx,.xls" onChange={e => { setImportFile(e.target.files?.[0] || null); setImportError(""); setImportResult(null); }} className="hidden" data-testid="import-enc-file-input" />
                <div className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${importFile ? "border-[#f7c935]/40 bg-[#f7c935]/5" : "border-white/10 hover:border-[#f7c935]/30"}`}>
                  {importFile ? (
                    <div className="flex flex-col items-center gap-2">
                      <FileSpreadsheet className="w-8 h-8 text-[#f7c935]" />
                      <span className="text-sm text-white font-bold">{importFile.name}</span>
                      <span className="text-[10px] text-slate-500">{(importFile.size / 1024).toFixed(1)} Ko</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <FileSpreadsheet className="w-8 h-8 text-slate-500" />
                      <span className="text-sm text-slate-400">Cliquez pour selectionner un fichier</span>
                      <span className="text-[10px] text-slate-600">CSV, XLSX ou XLS</span>
                    </div>
                  )}
                </div>
              </label>
              <div className="bg-[#0b0811] rounded-lg p-3 text-[10px] text-slate-500 space-y-1">
                <div className="font-bold text-slate-400 mb-1">Colonnes reconnues :</div>
                <div>Client | Financement | Statut/Reglement | Total TTC | Regle | Solde Financeurs | Date solde | N Devis/Factures | Info</div>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button onClick={handleClose} variant="outline" className="border-white/10 text-slate-400 h-9 text-xs">Annuler</Button>
                <Button onClick={handleImport} disabled={importing || !importFile} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold h-9 text-xs" data-testid="confirm-import-enc-btn">
                  {importing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ArrowRight className="w-4 h-4 mr-1" />}
                  Importer
                </Button>
              </div>
            </>
          )}

          {importResult && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 text-center">
              <CheckCircle2 className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <div className="text-sm font-bold text-green-400 mb-1">Import termine</div>
              <p className="text-xs text-slate-400">{importResult.count} encaissement(s) importe(s) depuis {importResult.filename || "le fichier"}</p>
              <Button onClick={handleClose} className="mt-3 bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs">Fermer</Button>
            </div>
          )}

          {importError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-xs" data-testid="import-enc-error">{importError}</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
